import SqFetch from 'application/fetch';

export default class CheckoutClient {
  constructor(orderNumber, storeCode, languageCode) {
    this.orderNumber = orderNumber;
    this.storeCode = storeCode;
    this.languageCode = languageCode;
    this.paymentPostalCode = '';
  }

  _urlContext() {
    return `?store_code=${this.storeCode}&language_code=${this.languageCode}&order_number=${this.orderNumber}`;
  }

  _requestContext() {
    return {
      store_code: this.storeCode,
      language_code: this.languageCode,
      order_number: this.orderNumber,
    };
  }

  _handleResponse(response) {
    if (!response.ok) {
      if (response.status === 400) {
        return Promise.reject(response.json());
      }
      throw new Error(response.statusText);
    }
    return response.json();
  }

  _handleCompletionResponse(response) {
    if (response.ok) {
      return response.json().catch(() => {
        const error = new Error('Invalid checkout completion response');
        Object.assign(error, {
          error_code: 'internal_error',
          http_status: response.status,
        });
        throw error;
      });
    }

    return response
      .json()
      .catch(() => ({}))
      .then((body) => {
        const errorBody = body && typeof body === 'object' ? body : {};
        const errorMessage =
          typeof errorBody.message === 'string'
            ? errorBody.message
            : response.statusText || 'Checkout request failed';
        const error = new Error(errorMessage);
        const errorCode =
          typeof errorBody.error_code === 'string'
            ? errorBody.error_code
            : typeof errorBody.data?.error_code === 'string'
              ? errorBody.data.error_code
              : undefined;

        Object.assign(error, {
          ...(errorCode ? { error_code: errorCode } : {}),
          http_status: response.status,
        });
        throw error;
      });
  }

  orderSummary() {
    return SqFetch.get(
      `/shop/api/v1/checkout/order/show${this._urlContext()}`,
      { cache: 'no-store' },
    ).then(this._handleResponse);
  }

  orderUpdate(checkoutInformation) {
    return SqFetch.post('/shop/api/v1/checkout/order/update', {
      checkout_information: checkoutInformation,
      ...this._requestContext(),
    }).then(this._handleResponse);
  }

  orderComplete(
    recaptchaResponse,
    financingDisclosureAcknowledged = false,
    useSavedPaymentSource = false,
    makeDefaultCard = false,
  ) {
    return SqFetch.post('/shop/api/v1/checkout/order/complete', {
      financing_disclosure_acknowledged: financingDisclosureAcknowledged,
      recaptcha_response: recaptchaResponse,
      payment_postal_code: this.paymentPostalCode,
      forter_token: window.ftrToken,
      use_saved_payment_source: useSavedPaymentSource,
      set_default_payment_source: makeDefaultCard,
      ...this._requestContext(),
    }).then(
      (response) => this._handleCompletionResponse(response),
      (error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : error &&
                typeof error === 'object' &&
                typeof error.message === 'string'
              ? error.message
              : 'Checkout request failed';
        const networkError =
          error instanceof Error ? error : new Error(errorMessage);

        if (error && typeof error === 'object') {
          Object.assign(networkError, error);
        }
        Object.assign(networkError, { error_code: 'network_failure' });
        return Promise.reject(networkError);
      },
    );
  }

  fetchShippingRates() {
    return SqFetch.get(
      `/shop/api/v1/checkout/shipping_rates/list${this._urlContext()}`,
    ).then(this._handleResponse);
  }

  selectShippingRate(id) {
    return SqFetch.post('/shop/api/v1/checkout/shipping_rates/update', {
      shipping_rate_id: id,
      ...this._requestContext(),
    }).then(this._handleResponse);
  }

  fetchPaymentOptions() {
    return SqFetch.get(
      `/shop/api/v1/checkout/payment_options/list${this._urlContext()}`,
    ).then(this._handleResponse);
  }

  selectPaymentOption(paymentOption) {
    return SqFetch.post('/shop/api/v1/checkout/payment_options/select', {
      payment_option: paymentOption,
      ...this._requestContext(),
    }).then(this._handleResponse);
  }

  setPaymentPostalCode(postalCode) {
    this.paymentPostalCode = postalCode;
  }

  fetchOffers() {
    return SqFetch.get(
      `/shop/api/v1/checkout/offers/list${this._urlContext()}`,
    ).then(this._handleResponse);
  }

  selectOffer(token, opts = {}) {
    return SqFetch.post(
      '/shop/api/v1/checkout/offers/select',
      {
        selected_offer_token: token,
        ...this._requestContext(),
      },
      opts,
    ).then(this._handleResponse);
  }

  previewOffer() {
    return SqFetch.get(
      `/shop/api/v1/checkout/offers/preview${this._urlContext()}`,
    ).then(this._handleResponse);
  }

  listAccessPoints(postalCode) {
    return SqFetch.get(
      `/shop/api/v1/checkout/access_points/list${this._urlContext()}&postal_code=${postalCode}`,
    ).then(this._handleResponse);
  }

  fetchLabels() {
    return SqFetch.get(
      `/shop/api/v1/checkout/order/labels${this._urlContext()}`,
    ).then(this._handleResponse);
  }

  createFinancingDisclosures(contractDate = new Date().toJSON()) {
    return SqFetch.post('/shop/api/v1/checkout/financing_disclosures/create', {
      contract_date: contractDate,
      ...this._requestContext(),
    }).then(this._handleResponse);
  }

  async validateTaxNumber(taxNumber) {
    const response = await SqFetch.post('/shop/api/v1/validate_tax_number', {
      country_code: this.storeCode,
      tax_number: taxNumber,
    });

    // allow if response is ok or server error
    return response && response.status !== 400;
  }

  async getSavedCards() {
    const response = await SqFetch.get('/shop/api/v1/saved-payment-sources');
    return this._handleResponse(response);
  }

  async deleteSavedCard(cardId) {
    const response = await SqFetch.delete(
      `/shop/api/v1/saved-payment-sources/${cardId}`,
    );
    return this._handleResponse(response);
  }
}
